const React = require("react");
const { MdArrowBack, MdDeleteOutline } = require("react-icons/md");
// catSelected.jsx/catSearch.jsx sont des fichiers .jsx sous src/ (transformés
// JSX normalement par webpack, peu importe que CE fichier-ci, hors src/, ne
// le soit pas -- même précédent que allPriceFeet.jsx ailleurs dans ce
// package). Import par défaut compilé par babel -> {__esModule, default}
// plutôt que le composant directement (voir interopDefault, même note que
// pour react-bootstrap/Modal dans CJSyncModal.js).
const interopDefault = (mod) => (mod && mod.default) ? mod.default : mod;
const CatSelected = interopDefault(require("../../../../src/includes/pages/categories/catSelected.jsx"));
const CatSearch = interopDefault(require("../../../../src/includes/pages/categories/catSearch.jsx"));
const { getCategoriesByID, getCategoriesByName } = require("../../../../src/includes/auth/apiCategory.js");
// activeInfoSource est déjà peuplé de façon synchrone par Routes.js
// (setElakayLanguageContext) avant que n'importe quelle page -- y compris
// celle-ci -- ne monte : pas besoin de reconstruire un infoSource compatible
// à la main, CatSelected/CatSearch fonctionnent avec le vrai singleton global.
const { getElakayLanguageContext } = require("../../../../src/includes/pageInside/dataElakay.jsx");
const h = React.createElement;

const ROOT_CATEGORY_ID = "5ed4946fd2ba8812c20261a8";

// CJ renvoie parfois un seul niveau déjà "aplati" en une chaîne du genre
// "Men's Clothing / T-Shirts / Geometric" ou "Home, Garden & Furniture >
// Home Storage > Adult Wellness" (categoryName complet dans un seul champ,
// oneCategoryName/twoCategoryName vides) plutôt que 3 champs séparés -- on
// éclate alors CE niveau par "/" OU ">" en plusieurs niveaux cochables
// individuels, au lieu d'une seule ligne au nom composite. Une virgule dans
// un nom ("Home, Garden & Furniture") n'est PAS un séparateur de niveau,
// donc jamais splittée.
const splitLevelName = (name) => String(name || "").split(/[/>]/).map((part) => part.trim()).filter(Boolean);

// Éclate dropshippingData.category en niveaux cochables séparés. `path[]`
// (jusqu'à 3 niveaux, {id?, name}) est la forme riche ajoutée dans
// cjDropshipping.service.js normalizeCJProduct ; un produit mis en cache
// AVANT ce changement n'a que {name, parentName, externalCategoryId} --
// repli sur ces 2 niveaux dans ce cas plutôt que de planter.
const buildCJLevels = (cjCategory) => {
  if (!cjCategory) return [];
  const rawLevels = Array.isArray(cjCategory.path) && cjCategory.path.length
    ? cjCategory.path
    : [
        cjCategory.parentName ? { name: cjCategory.parentName } : null,
        cjCategory.name ? { id: cjCategory.externalCategoryId, name: cjCategory.name } : null,
      ].filter(Boolean);

  const expanded = [];
  rawLevels.forEach((level, levelIndex) => {
    const parts = splitLevelName(level.name);
    if (parts.length <= 1) { expanded.push(level); return; }
    parts.forEach((part, partIndex) => {
      // Seul le tout dernier segment du tout dernier niveau CJ garde l'id
      // réel (c'est la feuille) -- les segments intermédiaires nés de
      // l'éclatement n'ont pas d'id CJ propre.
      const isLeaf = levelIndex === rawLevels.length - 1 && partIndex === parts.length - 1;
      expanded.push({ id: isLeaf ? level.id : undefined, name: part });
    });
  });

  const names = expanded.map((level) => level.name);
  return expanded.map((level, index) => ({
    // Suffixé par l'index : plusieurs niveaux sans id (le cas courant après
    // éclatement) peuvent partager le même nom, `key` doit rester unique par
    // POSITION dans le chemin, jamais uniquement par nom -- voir groupKeyOf
    // ci-dessous, qui lit ce même `key` depuis le groupe stocké plutôt que
    // de le recalculer (recalcul = même risque de collision).
    key: level.id || `level-${index}-${level.name}`,
    name: level.name,
    externalCategoryId: level.id || "",
    fullPath: names.slice(0, index + 1).join(" › "),
  }));
};

const groupKeyOf = (group) => group.cjCategory.key;

// Étape 1 du CJSyncModal. Chaque niveau du chemin CJ (path[]) est une "CJ
// category" cochable indépendamment ; cocher un niveau ouvre un mapping
// dédié vers une ou plusieurs catégories Elakay pour CE niveau précis (voir
// `value` : [{cjCategory, elakayCategories}]). Réutilise CatSelected/
// CatSearch et la navigation getcategories/loadCategory/clickBack/
// seleteCategory/ButtonBackCategory d'EditProduct (editProduct.jsx ~L1550-
// L1941), un seul picker actif à la fois (accordéon par niveau).
//
// Tout reste local -- rien n'est persisté ici (contrairement à clickSubCat/
// linkCTP dans EditProduct, où un produit existe déjà) : CJSyncModal
// n'écrit qu'au clic final "Finalize Synchronization".
const CJCategoryMappingStep = ({ cjCategory, value, onChange, onValidChange, t }) => {
  const infoSource = getElakayLanguageContext();
  const levels = React.useMemo(() => buildCJLevels(cjCategory), [cjCategory]);
  const groups = value || [];

  const [activePickerKey, setActivePickerKey] = React.useState(null);
  const [getcategories, setCategories] = React.useState({});
  const [catSelectedSearch, setCatSelectedSearch] = React.useState(false); // false = "by name search" (CatSearch), même défaut qu'EditProduct
  const [search, setSearch] = React.useState([]);
  const [searchInput, setSearchInput] = React.useState("");
  // "searching" | "found" | "not-found" | null -- alimenté par onSearchStateChange
  // (voir catSearch.jsx) plutôt que déduit de `search.length` seul, pour
  // éviter un faux "Not found" pendant le debounce/fetch en cours.
  const [searchStatus, setSearchStatus] = React.useState(null);

  const findGroup = (levelKey) => groups.find((g) => groupKeyOf(g) === levelKey);

  const valid = groups.length > 0 && groups.every((g) =>
    g.elakayCategories.length > 0
    && g.elakayCategories.every((e) => e.catID && Array.isArray(e.gategoriesLink) && e.gategoriesLink.length > 0)
  );

  React.useEffect(() => {
    onValidChange && onValidChange(valid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valid]);

  const loadCategory = async (categoryID) => {
    if (!categoryID) return;
    const data = await getCategoriesByID(categoryID).catch((e) => ({ error: e.message }));
    if (data?.error || !data?.cartegory) return;
    setCategories(data.cartegory);
    setSearch([]);
    setSearchInput("");
  };

  // Recherche automatiquement le nom du niveau CJ coché parmi les catégories
  // Elakay dès l'ouverture du picker (mode Search par défaut) -- le seller
  // voit tout de suite si une catégorie Elakay du même nom existe déjà
  // (badge vert "Found") ou non (badge rouge "Not found") avant de chercher
  // lui-même autre chose.
  const openPicker = (level) => {
    setActivePickerKey(level.key);
    setCategories({});
    setCatSelectedSearch(false);
    setSearch([]);
    setSearchInput(level.name || "");
    setSearchStatus(level.name ? "searching" : null);
  };

  const handleSearchStateChange = ({ searching, results, error }) => {
    if (searching) { setSearchStatus("searching"); return; }
    if (error) { setSearchStatus(null); return; }
    setSearchStatus(results && results.length ? "found" : "not-found");
  };

  React.useEffect(() => {
    if (activePickerKey && !getcategories._id) loadCategory(ROOT_CATEGORY_ID);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePickerKey]);

  const clickBack = (categoryID) => loadCategory(categoryID);
  const seleteCategory = () => (event) => { event.preventDefault(); loadCategory(event.target.value); };
  // Réplique exacte de ButtonBackCategory (editProduct.jsx ~L1580) : saute au
  // 2e maillon du breadcrumb (categoryID[1]), pas "un niveau au-dessus" --
  // comportement d'origine conservé tel quel plutôt que "corrigé" ici.
  const ButtonBackCategory = () => h("span", {
    onClick: () => clickBack(getcategories.categoryID?.length > 1 ? getcategories.categoryID[1] : ROOT_CATEGORY_ID),
    className: "cjwiz-cat-back", role: "button", tabIndex: 0,
  }, h(MdArrowBack));

  const toggleLevel = (level, checked) => {
    if (checked) {
      if (findGroup(level.key)) return;
      onChange([...groups, {
        cjCategory: { key: level.key, externalCategoryId: level.externalCategoryId, name: level.name, fullPath: level.fullPath },
        elakayCategories: [],
      }]);
    } else {
      onChange(groups.filter((g) => groupKeyOf(g) !== level.key));
      if (activePickerKey === level.key) setActivePickerKey(null);
    }
  };

  // Dédoublonnage scopé à CE groupe seulement -- la même catégorie Elakay
  // reste autorisée sous un AUTRE niveau CJ (voir point "ne jamais dupliquer
  // deux fois le même catID dans le même mapping", pas globalement). `category`
  // accepte aussi bien un noeud navigué via CatSelected/CatSearch
  // (`getcategories`, {_id, name, categoryID}) qu'un résultat de recherche
  // brut (même forme -- cartegoryShearch projette déjà name/categoryID, pas
  // besoin d'un fetch getCategoriesByID supplémentaire pour ceux-ci).
  const addElakayCategoryToGroup = (levelKey, category) => {
    if (!category?._id) return;
    onChange(groups.map((g) => {
      if (groupKeyOf(g) !== levelKey) return g;
      if (g.elakayCategories.some((e) => String(e.catID) === String(category._id))) return g;
      return {
        ...g,
        elakayCategories: [...g.elakayCategories, {
          catID: category._id,
          gategoriesLink: category.categoryID || [],
          name: category.name || [],
        }],
      };
    }));
  };

  const handleLinkCategory = (levelKey) => addElakayCategoryToGroup(levelKey, getcategories);

  const handleRemoveElakay = (levelKey, catID) => {
    onChange(groups.map((g) => groupKeyOf(g) !== levelKey
      ? g
      : { ...g, elakayCategories: g.elakayCategories.filter((e) => String(e.catID) !== String(catID)) }));
  };

  const isCatIDInGroup = (levelKey, catID) => {
    const group = findGroup(levelKey);
    return Boolean(group?.elakayCategories.some((e) => String(e.catID) === String(catID)));
  };
  const isDuplicateInGroup = (levelKey) => isCatIDInGroup(levelKey, getcategories._id);

  return h("div", { className: "cjwiz-step" },
    h("div", { className: "cjwiz-step__intro" },
      h("h3", null, t("h_cjwiz_step1_title", "Choose Elakay categories")),
      h("p", { className: "cjds-muted" }, t(
        "h_cjwiz_step1_desc",
        "Check each CJ category level, then link one or more matching Elakay categories to it."
      ))
    ),

    !levels.length ? h("p", { className: "cjds-muted" }, t("h_cjwiz_cat_no_cj_category", "This CJ product has no category information.")) : h("div", { className: "cjwiz-cj-levels" },
      ...levels.map((level) => {
        const group = findGroup(level.key);
        const checked = Boolean(group);
        const mapped = Boolean(group?.elakayCategories.length);
        const pickerOpenHere = activePickerKey === level.key;

        return h("div", { key: level.key, className: `cjwiz-cj-level${checked ? " cjwiz-cj-level--checked" : ""}` },
          h("label", { className: "cjwiz-cj-level__head" },
            h("input", { type: "checkbox", checked, onChange: (e) => toggleLevel(level, e.target.checked) }),
            h("div", { className: "cjwiz-cj-level__text" },
              h("strong", null, level.name),
              h("span", { className: "cjwiz-cj-level__path" }, level.fullPath)
            ),
            level.externalCategoryId ? h("span", { className: "cjds-muted cjwiz-cj-level__extid" }, `ID: ${level.externalCategoryId}`) : null,
            h("span", { className: "cjwiz-badge cjwiz-badge--muted" }, t("h_cjwiz_cat_source_cj", "Source: CJ")),
            h("span", { className: `cjwiz-badge ${mapped ? "cjwiz-badge--match" : "cjwiz-badge--missing"}` },
              mapped ? t("h_cjwiz_cat_mapped", "Mapped") : t("h_cjwiz_cat_not_mapped", "Not mapped"))
          ),

          checked ? h("div", { className: "cjwiz-cj-level__body" },
            group.elakayCategories.length ? h("div", { className: "cjwiz-cat-chips" },
              ...group.elakayCategories.map((entry) => h("span", { key: entry.catID, className: "cjwiz-cat-chip" },
                h("input", { type: "checkbox", checked: true, readOnly: true }),
                (entry.name || []).join(" › ") || t("h_cjwiz_cat_unresolved", "(category)"),
                h("button", { type: "button", onClick: () => handleRemoveElakay(level.key, entry.catID), "aria-label": t("h_cjwiz_cat_remove", "Remove") },
                  h(MdDeleteOutline))
              ))
            ) : h("p", { className: "cjds-muted" }, t("h_cjwiz_cat_group_empty", "No Elakay category selected")),

            !pickerOpenHere ? h("button", { type: "button", className: "cjwiz-add-cat-btn", onClick: () => openPicker(level) },
              `+ ${t("h_cjwiz_cat_add_button", "Add Elakay Category")}`
            ) : h("div", { className: "cjwiz-cat-picker-panel" },
              h("div", { className: "cjwiz-cat-picker-panel__tabs" },
                h("button", {
                  type: "button",
                  className: `cjwiz-cat-picker__tab${catSelectedSearch ? " active" : ""}`,
                  onClick: () => { setCatSelectedSearch(true); setSearchStatus(null); },
                }, t("h_cjwiz_cat_browse", "Browse")),
                h("button", {
                  type: "button",
                  className: `cjwiz-cat-picker__tab${!catSelectedSearch ? " active" : ""}`,
                  onClick: () => setCatSelectedSearch(false),
                }, t("h_cjwiz_cat_search", "Search")),
                !catSelectedSearch && searchStatus && searchStatus !== "searching" ? h("span", {
                  className: `cjwiz-badge ${searchStatus === "found" ? "cjwiz-badge--success" : "cjwiz-badge--error"}`,
                }, searchStatus === "found" ? t("h_cjwiz_cat_found", "Found") : t("h_cjwiz_cat_not_found", "Not found")) : null,
                !catSelectedSearch && searchStatus === "searching" ? h("span", { className: "cjds-muted" }, t("h_cjwiz_cat_searching", "Searching...")) : null,
                h("button", { type: "button", className: "cjwiz-cat-picker__close", onClick: () => setActivePickerKey(null) }, "×")
              ),

              catSelectedSearch ? h(CatSelected, {
                ButtonBackCategory,
                clickBack,
                seleteCategory,
                laguangeElakay: infoSource?.laguangeElakay,
                infoSource,
                categories: getcategories,
              }) : h(CatSearch, {
                infoSource,
                search,
                setSearch,
                setSearchInput,
                searchInput,
                getCategoriesByName,
                categories: getcategories,
                loadCategory,
                onSearchStateChange: handleSearchStateChange,
              }),

              // Liste explicite et complète des catégories Elakay trouvées
              // (nom entier, pas tronqué) avec ajout en un clic -- CatSearch
              // affiche déjà ses propres résultats au-dessus, mais tronque le
              // nom de la catégorie elle-même dans son rendu (garde
              // uniquement les ancêtres). Celle-ci reste la liste de
              // référence pour voir et ajouter tout ce qui a été trouvé.
              !catSelectedSearch && searchStatus === "found" && search.length ? h("div", { className: "cjwiz-cat-found-list" },
                h("p", { className: "cjwiz-cat-found-list__label" },
                  t("h_cjwiz_cat_found_list_label", "Elakay categories found:")),
                ...search.map((cat) => h("div", { key: cat._id, className: "cjwiz-cat-row" },
                  h("span", { className: "cjwiz-cat-row__path" }, (cat.name || []).join(" › ")),
                  h("button", {
                    type: "button",
                    className: "cjds-btn cjwiz-btn-sm",
                    disabled: isCatIDInGroup(level.key, cat._id),
                    onClick: () => addElakayCategoryToGroup(level.key, cat),
                  }, isCatIDInGroup(level.key, cat._id)
                    ? t("h_cjwiz_cat_already_linked", "Already linked")
                    : t("h_cjwiz_cat_add", "Add"))
                ))
              ) : null,

              h("div", { className: "cjwiz-cat-picker-panel__footer" },
                getcategories?.name?.length ? h("span", { className: "cjds-muted" },
                  `${t("h_cjwiz_cat_current", "Current")}: ${(getcategories.name || []).join(" › ")}`) : null,
                h("button", {
                  type: "button",
                  className: "cjds-btn cjwiz-link-btn",
                  disabled: !getcategories._id || isDuplicateInGroup(level.key),
                  onClick: () => handleLinkCategory(level.key),
                }, isDuplicateInGroup(level.key)
                  ? t("h_cjwiz_cat_already_linked", "Already linked")
                  : t("h_cjwiz_cat_link", "Link Category"))
              )
            )
          ) : null
        );
      })
    ),

    h("div", { className: `cjwiz-step__status ${valid ? "cjwiz-step__status--ready" : ""}` },
      valid
        ? h("span", { className: "cjwiz-badge cjwiz-badge--success" }, t("h_cjwiz_step1_ready", "Categories ready"))
        : h("span", { className: "cjds-muted" }, t(
            "h_cjwiz_step1_not_ready",
            "Check at least one CJ category and link a valid Elakay category to each checked level."
          ))
    )
  );
};

module.exports = { CJCategoryMappingStep, ROOT_CATEGORY_ID, buildCJLevels };
